/**
 * The channel's link to Twitch, for its owner.
 *
 * Two jobs. Say plainly when the channel's consent is out of date — a scope
 * tomo started asking for after the channel connected is a feature that
 * quietly never works (L42), and only the owner can fix it, by connecting
 * again. And hold the switch for mod tools, which is off until the owner
 * turns it on: the mod feed carries held messages and unban requests, and
 * routing those through tomo is the channel's decision (ADR 0050 §4).
 */
import { useState, type ReactNode } from 'react';
import type { ChannelModerator, TwitchConnection } from '@tomo/contracts';
import { Button } from './primitives.jsx';
import { Check } from './form.jsx';
import { twitchConnection } from '../lib/twitchConnection.js';
import { hrefFor } from '../lib/router.js';
import { useAsync } from '../useAsync.js';
import { useFeature } from '../Session.jsx';

export function TwitchPanel({ channelId }: { channelId: string }): ReactNode {
  const [result, retry] = useAsync(
    (signal) => twitchConnection.get(channelId, signal), [channelId],
  );
  const [saved, setSaved] = useState<TwitchConnection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (result.state === 'loading') return null;
  if (result.state === 'failed') {
    return (
      <Section>
        <p className="text-sm text-ink-400">
          Could not read this channel&apos;s Twitch connection.{' '}
          <button type="button" className="underline" onClick={retry}>Try again</button>
        </p>
      </Section>
    );
  }

  const connection = saved ?? result.value;

  const reconnect = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      window.location.assign(await twitchConnection.start(hrefFor([channelId])));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the Twitch connection.');
      setBusy(false);
    }
  };

  const toggle = async (enabled: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setSaved(await twitchConnection.patch(channelId, { mod_tools_enabled: enabled }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section>
      <p className="text-sm text-ink-200">
        Connected as <span className="text-ink-50">twitch.tv/{connection.login}</span>
      </p>
      <EventsLine events={connection.events} />

      {connection.scopes_missing.length > 0 ? (
        <div className="mt-3 rounded-md border border-warn-400/40 bg-warn-400/5 p-3 text-sm">
          <p className="text-warn-400">
            tomo needs a permission this channel has not granted yet, so some things
            for this channel will not arrive until you connect again.
          </p>
          <p className="mt-1 text-xs text-ink-400">
            Missing: {connection.scopes_missing.join(', ')}
          </p>
          <div className="mt-3">
            <Button onClick={() => { void reconnect(); }} disabled={busy}>
              Reconnect Twitch
            </Button>
          </div>
        </div>
      ) : null}

      <div className="mt-4">
        <Check
          checked={connection.mod_tools_enabled}
          onChange={(v) => { void toggle(v); }}
          label="Let this channel's Twitch moderators use tomo's mod tools"
        />
        <p className="mt-1 pl-6 text-xs text-ink-400">
          Mod tools are still being built. Turning this on now means your moderators
          get them as soon as they ship. Twitch still decides who is a moderator.
        </p>
      </div>

      <Moderators channelId={channelId} />

      {error ? <p className="mt-3 text-sm text-bad-400">{error}</p> : null}
    </Section>
  );
}

/**
 * Whether triggers can fire at all.
 *
 * The one line an owner needs before authoring anything: a trigger on a
 * channel Twitch is not delivering to is a trigger that silently never pays.
 */
function EventsLine({ events }: { events: TwitchConnection['events'] }): ReactNode {
  // Triggers are behind the `collectibles` flag, so without it the line
  // reports the connection and does not name a feature nobody can see.
  const triggers = useFeature('collectibles');
  if (events === null) {
    return <p className="mt-1 text-xs text-ink-400">Checking whether Twitch is sending events…</p>;
  }
  if (events.status === 'receiving') {
    return (
      <p className="mt-1 text-xs text-good-400">
        {triggers ? 'Receiving events from Twitch, so your triggers can fire.' : 'Receiving events from Twitch.'}
      </p>
    );
  }
  return (
    <p className="mt-1 text-xs text-bad-400">
      {events.status === 'revoked'
        ? 'Twitch stopped sending events because tomo was disconnected there. Reconnect Twitch to fix it.'
        : triggers
          ? 'Twitch is not sending some events yet, so some triggers will not fire. tomo keeps retrying; reconnecting usually fixes it.'
          : 'Twitch is not sending some events yet. tomo keeps retrying; reconnecting usually fixes it.'}
    </p>
  );
}

/**
 * The channel's moderators who use tomo, and the owner's override.
 *
 * Suspending switches somebody's tools off HERE, whatever Twitch says. It
 * does not unmod them on Twitch, and the copy says so, because an owner who
 * thinks it did has not dealt with the thing they meant to.
 */
function Moderators({ channelId }: { channelId: string }): ReactNode {
  const [result, retry] = useAsync(
    (signal) => twitchConnection.moderators(channelId, signal), [channelId],
  );
  const [changed, setChanged] = useState<Record<string, ChannelModerator>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (result.state === 'loading') return null;
  if (result.state === 'failed') {
    return (
      <p className="mt-4 text-xs text-ink-400">
        Could not load this channel&apos;s moderators.{' '}
        <button type="button" className="underline" onClick={retry}>Try again</button>
      </p>
    );
  }

  const mods = result.value.map((m) => changed[m.uid] ?? m);
  if (mods.length === 0) {
    return (
      <p className="mt-4 text-xs text-ink-400">
        None of your Twitch moderators use tomo yet. They appear here once they turn on mod tools
        from a tomo room.
      </p>
    );
  }

  const toggle = async (mod: ChannelModerator): Promise<void> => {
    setBusy(mod.uid);
    setError(null);
    try {
      const next = await twitchConnection.suspend(channelId, mod.uid, mod.status !== 'suspended');
      setChanged((c) => ({ ...c, [mod.uid]: next }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that.');
    } finally {
      setBusy(null);
    }
  };

  const label: Record<ChannelModerator['status'], string> = {
    active: 'Using mod tools',
    ended: 'Not a moderator on Twitch right now',
    suspended: 'Suspended on tomo',
  };

  return (
    <div className="mt-6">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-400">Moderators on tomo</h3>
      <ul className="mt-2 divide-y divide-ink-800">
        {mods.map((mod) => (
          <li key={mod.uid} className="flex items-center justify-between gap-3 py-2">
            <span className="min-w-0">
              <span className="block truncate text-sm text-ink-50">{mod.display_name ?? mod.handle ?? mod.uid}</span>
              <span className="block text-xs text-ink-400">{label[mod.status]}</span>
            </span>
            {mod.status === 'ended' ? null : (
              <Button variant="ghost" disabled={busy === mod.uid} onClick={() => { void toggle(mod); }}>
                {mod.status === 'suspended' ? 'Restore' : 'Suspend'}
              </Button>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-ink-400">
        Suspending only switches off their tools on tomo. It does not unmod them on Twitch.
      </p>
      {error ? <p className="mt-2 text-sm text-bad-400">{error}</p> : null}
    </div>
  );
}

function Section({ children }: { children: ReactNode }): ReactNode {
  return (
    <section className="mt-10 rounded-lg border border-ink-800 p-5">
      <h2 className="text-sm font-semibold text-ink-50">Twitch</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}
